// v2.71.77 — Centralized ErrorBoundary for the whole app.
//
// Why this exists
// ───────────────
// Before this file the codebase had ZERO error boundaries. Any uncaught
// render error or lazy-chunk load failure would propagate to the React
// root and unmount everything → the dreaded "white screen of death".
//
// Common triggers in the wild:
//   1. ChunkLoadError after a redeploy — user has an old build open, then
//      navigates to a route whose lazy chunk has a NEW hash. The fetch
//      404s, Suspense bubbles the error up, no boundary catches it.
//   2. Render crash inside a single feature (e.g. PropertyDetail crashes
//      on a malformed image URL). Without a per-page boundary the entire
//      app dies.
//   3. setState during render → React throws on commit → entire tree
//      unmounts.
//   4. Third-party widget exceptions (Razorpay popup, Leaflet map, etc.).
//
// Strategy
// ────────
//   • <RootErrorBoundary>  — wraps the whole app, last-resort UI.
//   • <PageErrorBoundary>  — wraps each top-level page (PropertyDetail,
//                            MapView, Dashboard). Lets the user navigate
//                            away from a broken page without losing the
//                            shell (Header / Footer / Nav).
//   • ChunkLoadError → "App updated, refresh" auto-prompt (most common
//                       cause on a production with frequent deploys).
//   • Other errors → "Something went wrong" + Retry + Go Home + console
//                    log of the stack for debugging.
//
// Best-practice notes
// ───────────────────
//   • Error boundaries must be CLASS components (React requirement).
//   • `getDerivedStateFromError` updates state synchronously so the
//     fallback renders on the next paint.
//   • `componentDidCatch` is the side-effect hook — logging only.
//   • We deliberately do NOT swallow the error from the console; logging
//     it loud helps the dev team diagnose in production.

import React from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "../../components/ui/button";

/**
 * Is this error caused by a stale lazy-chunk reference (post-deploy)?
 * Webpack 5 throws `ChunkLoadError` with name === "ChunkLoadError"; older
 * builds may throw a generic Error whose message contains "Loading chunk"
 * or "Failed to fetch dynamically imported module".
 */
function isChunkLoadError(err) {
  if (!err) return false;
  const name = String(err.name || "");
  const msg = String(err.message || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

class ErrorBoundaryBase extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Loud console log — keeps the developer happy without spamming UI.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary:${this.props.scope || "global"}]`,
      error,
      info?.componentStack
    );
    this.setState({ info });
    if (isChunkLoadError(error)) {
      // Stale-chunk → force a single hard reload to pick up the new build.
      // Use a guard so we don't reload-loop if the new build also crashes.
      try {
        const flagKey = `he-reloaded-for-chunk-${this.props.scope || "global"}`;
        if (!sessionStorage.getItem(flagKey)) {
          sessionStorage.setItem(flagKey, "1");
          window.location.reload();
        }
      } catch (_) {
        /* sessionStorage unavailable — silent */
      }
    }
  }

  handleRetry = () => {
    // Clear the boundary state so the children try to render again.
    this.setState({ error: null, info: null });
  };

  handleHardReload = () => {
    try {
      // Bust any sessionStorage flags that prevented auto-reload.
      sessionStorage.removeItem(`he-reloaded-for-chunk-${this.props.scope || "global"}`);
    } catch (_) { /* noop */ }
    window.location.reload();
  };

  handleGoHome = () => {
    // Don't use react-router — the router may be broken. Hard navigation.
    window.location.href = "/";
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback, scope = "global" } = this.props;

    // Allow caller-supplied fallback render fn.
    if (typeof fallback === "function") {
      return fallback({ error, retry: this.handleRetry });
    }

    const isChunk = isChunkLoadError(error);
    const title = isChunk ? "The app was updated" : "Something went wrong";
    const desc = isChunk
      ? "We're refreshing automatically to load the new version. If this message stays, tap the button below."
      : "We hit an unexpected error on this page. You can retry, go home, or refresh to recover.";

    return (
      <div
        className="min-h-[60vh] flex items-center justify-center px-6 py-12 bg-[#FAFAFA]"
        data-testid={`error-boundary-${scope}`}
        role="alert"
      >
        <div className="max-w-md w-full text-center bg-white border border-[#E4E4E7] p-8 rounded-sm shadow-sm">
          <AlertTriangle
            size={28}
            className={`mx-auto mb-3 ${isChunk ? "text-amber-600" : "text-[#DC2626]"}`}
          />
          <h1 className="font-heading font-bold text-2xl tracking-tight text-[#09090B]">
            {title}
          </h1>
          <p className="text-[13px] text-[#52525B] mt-2.5 mb-5">{desc}</p>

          {/* Error message — kept terse, full stack only in console. */}
          {!isChunk && error?.message && (
            <details className="text-left bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-2 mb-5">
              <summary className="text-[11px] uppercase tracking-wider text-[#71717A] font-semibold cursor-pointer">
                Technical details
              </summary>
              <pre className="text-[10.5px] text-[#52525B] mt-2 whitespace-pre-wrap break-words">
                {String(error.message).slice(0, 400)}
              </pre>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            {!isChunk && (
              <Button
                onClick={this.handleRetry}
                variant="outline"
                className="rounded-sm"
                data-testid={`error-boundary-${scope}-retry`}
              >
                <RefreshCw size={14} className="mr-1.5" /> Retry
              </Button>
            )}
            <Button
              onClick={this.handleHardReload}
              className="btn-brand rounded-sm"
              data-testid={`error-boundary-${scope}-reload`}
            >
              <RefreshCw size={14} className="mr-1.5" /> Refresh page
            </Button>
            {!isChunk && (
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="rounded-sm"
                data-testid={`error-boundary-${scope}-home`}
              >
                <Home size={14} className="mr-1.5" /> Home
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Root-level boundary. Wraps the entire app. Last-resort.
 */
export function RootErrorBoundary({ children }) {
  return <ErrorBoundaryBase scope="root">{children}</ErrorBoundaryBase>;
}

/**
 * Page-level boundary. Wrap each route's top page so a crash in one page
 * doesn't take down the shell (Header/Nav/Footer).
 *
 * Usage:
 *   <PageErrorBoundary scope="property-detail">
 *     <PropertyDetail />
 *   </PageErrorBoundary>
 */
export function PageErrorBoundary({ scope, children, fallback }) {
  return (
    <ErrorBoundaryBase scope={scope} fallback={fallback}>
      {children}
    </ErrorBoundaryBase>
  );
}

export default ErrorBoundaryBase;
